import { useState } from 'react'
import { cn } from '../utils'
import { Mail, X } from 'lucide-react'
import { motion } from 'framer-motion'
import type { SentInvitation, OrgInvitationStatus } from './types'

export type SentInvitationsListProps = {
  invitations: SentInvitation[]
  onCancelInvitation?: (invitationId: string) => Promise<void>
  className?: string
}

function StatusBadge({ status }: { status: OrgInvitationStatus }) {
  const styles: Record<OrgInvitationStatus, string> = {
    pending: 'bg-status-warning-surface text-status-warning-foreground',
    accepted: 'bg-status-success-surface text-status-success-foreground',
    expired: 'bg-secondary text-muted-foreground',
    declined: 'bg-destructive/10 text-destructive',
    cancelled: 'bg-secondary text-muted-foreground',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
        styles[status]
      )}
    >
      {status}
    </span>
  )
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const itemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.25,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
}

export function SentInvitationsList({ invitations, onCancelInvitation, className }: SentInvitationsListProps) {
  const [cancellingId, setCancellingId] = useState<string | null>(null)

  if (invitations.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2">No invitations sent yet.</p>
    )
  }

  const handleCancel = async (id: string) => {
    if (!onCancelInvitation) return
    setCancellingId(id)
    try {
      await onCancelInvitation(id)
    } finally {
      setCancellingId(null)
    }
  }

  return (
    <div className={cn('space-y-1', className)}>
      {invitations.map((inv, i) => (
        <motion.div
          key={inv.id}
          className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-card transition-colors"
          custom={i}
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="flex-shrink-0 rounded-full bg-secondary p-1.5">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <p className="flex-1 text-sm text-foreground truncate">{inv.email}</p>
          <StatusBadge status={inv.status} />
          <span className="text-xs text-muted-foreground/60 flex-shrink-0">
            Expires {formatDate(inv.expiresAt)}
          </span>
          {onCancelInvitation && (
            inv.status === 'pending' ? (
              <button
                type="button"
                onClick={() => handleCancel(inv.id)}
                disabled={cancellingId === inv.id}
                className="flex-shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
              >
                <X className="h-3 w-3" />
                {cancellingId === inv.id ? 'Cancelling…' : 'Cancel'}
              </button>
            ) : (
              <div className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px]" aria-hidden>
                <X className="h-3 w-3 opacity-0" />
                <span className="opacity-0 font-medium uppercase tracking-wider">Cancel</span>
              </div>
            )
          )}
        </motion.div>
      ))}
    </div>
  )
}
